import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RcaThunkArg } from "../../actions/actionTypes";
import { ProofOfWorkVerificationState } from "./ProofOfWorkVerificationState";
import { HttpError } from "../../services/HttpClient";

/**
 * The state for the proof-of-work widget
 */
export interface IProofOfWorkState {
  /**
   * The current verification state
   */
  verificationState: ProofOfWorkVerificationState;
}

/**
 * The initial state
 */
export const initialState: IProofOfWorkState = {
  verificationState: ProofOfWorkVerificationState.idle,
};

/**
 * Checks whether the user already has a valid proof-of-work proof cookie.
 * If so, sets the state to verified so the widget is skipped.
 */
const checkExistingCookie = createAsyncThunk<boolean, void, RcaThunkArg<HttpError>>(
  "proofOfWork/checkExistingCookie",
  async (_arg, thunkApi) => {
    const response = await thunkApi.extra.proofOfWorkService.getExpiryTimeAsync();
    if (response.hasValidCookie && response.expiration && new Date(response.expiration) > new Date()) {
      return true;
    }
    return false;
  }
);

const proofOfWorkSlice = createSlice({
  name: "proofOfWork",
  initialState,
  reducers: {
    /**
     * Maps an altcha-widget state string to the ProofOfWorkVerificationState enum.
     * Called from ProofOfWorkWidget on the widget's statechange event.
     *
     * @param state - the current proof-of-work state
     * @param action - the action containing the widget state string
     */
    setWidgetState: (state, action: PayloadAction<string>) => {
      const mapped = Object.values(ProofOfWorkVerificationState).find((v) => (v as string) === action.payload);
      state.verificationState = mapped ?? ProofOfWorkVerificationState.idle;
    },

    /**
     * Resets the proof-of-work state to idle so the widget re-solves.
     * Dispatched when the server rejects the proof (e.g., after a signing-key rotation).
     *
     * @param state - the current proof-of-work state
     */
    resetVerification: (state) => {
      state.verificationState = ProofOfWorkVerificationState.idle;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(checkExistingCookie.fulfilled, (state, action) => {
      if (action.payload) {
        state.verificationState = ProofOfWorkVerificationState.verified;
      }
    });
  },
});

export const actions = {
  ...proofOfWorkSlice.actions,
  checkExistingCookie,
};

export default proofOfWorkSlice.reducer;
